import React, {
  useState,
  useEffect
} from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Badge,
  Paper,
  Chip,
  Tooltip,
  Toolbar,
  IconButton,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  AppBar,
  Slide,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import {
  Alert
} from '@material-ui/lab';
import DocumentIcon from '@material-ui/icons/Description';
import DownloadIcon from '@material-ui/icons/GetApp';
import CloseIcon from '@material-ui/icons/Close';

import {IconByContentType} from './IconByContentType';
import {PDFAnnotationView} from './PDFAnnotationView';

import getDocumentType from '../../../utils/getDocumentType';
import localization from '../../../utils/localizations';
import {
  Document
} from '../../../types';

const useStyles = makeStyles((theme) => ({
  root: {
    
  },
  documentPreviewContentContainer: {
    display: 'block',
    textAlign: 'center',
    marginBottom: 10,
  },
  documentPreviewFilename: {
    color: '#777',
  },
  imagePreview: {
    width: '100%'
  },
  dialogAppBar: {
    position: 'relative',
  },
  dialogAppBarTitle: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  noPadding: {
    padding: 0,
  }
}));

function PreviewPDFDocument(props: {doc: Document; showSimilarity?: boolean;}) {
  const classes = useStyles();
  return (
    <PDFAnnotationView doc={props.doc} readOnly showSimilarity={props.showSimilarity} />
  );
}

function PreviewImageDocument(props: {doc: Document}) {
  const classes = useStyles();
  return (
    <div>
      <ButtonBase
        className={classes.documentPreviewContentContainer}
        href={props.doc.file}
        target="_blank"
      >
        <img src={props.doc.file} alt={props.doc.file_name} className={classes.imagePreview} />
      </ButtonBase>
    </div>
  );
}

function PreviewUnknownDocument(props: {doc: Document}) {
  const classes = useStyles();
  return (
    <div>
      <ButtonBase
        className={classes.documentPreviewContentContainer}
        href={props.doc.file}
        target="_blank"
      >
        <IconByContentType contentType={props.doc.content_type} size="large" />
        <Typography variant="body1" className={classes.documentPreviewFilename}>{props.doc.file_name}</Typography>
      </ButtonBase>
      <Alert
        severity="warning"
        action={
          <Button
            color="inherit"
            size="small"
            href={props.doc.file}
            target="_blank"
            startIcon={<DownloadIcon />}
          >
            {localization.download}
          </Button>
        }
      >{localization.previewUnsupportedForThisFile}</Alert>
    </div>
  );
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function UnsupportedFileTypeDialog(props: {doc: Document; open: boolean; onClose: () => void}) {
  const classes = useStyles();
  const documentType = getDocumentType(props.doc.content_type)
  const preview = (<PreviewUnknownDocument doc={props.doc} />);
  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose()}
      aria-labelledby="document-detail-dialog-title"
      aria-describedby="document-detail-dialog-description"
      TransitionComponent={Transition}
    >
      <DialogTitle id="document-detail-dialog-title">{props.doc.kind ? props.doc.kind : ''}</DialogTitle>
      <DialogContent>
        {preview}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.onClose()} color="primary">
          {localization.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function SupportedFileTypeDialog(props: {
  doc: Document;
  open: boolean;
  onClose: () => void;
  showSimilarity?: boolean;
}) {
  const classes = useStyles();
  const documentType = getDocumentType(props.doc.content_type)
  let preview;
  if (documentType == 'image') preview = (<PreviewImageDocument doc={props.doc} />);
  else if (documentType == 'pdf') preview = (<PreviewPDFDocument doc={props.doc} showSimilarity={props.showSimilarity} />);
  else preview = (<PreviewUnknownDocument doc={props.doc} />);
  return (
    <Dialog
      open={props.open}
      fullScreen
      onClose={() => props.onClose()}
      aria-labelledby="document-detail-dialog-title"
      aria-describedby="document-detail-dialog-description"
      TransitionComponent={Transition}
    >
      <AppBar className={classes.dialogAppBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={props.onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={classes.dialogAppBarTitle}>
            {props.doc.kind ? props.doc.kind : props.doc.file_name}
          </Typography>
          <Button
            color="inherit"
            size="small"
            href={props.doc.file}
            target="_blank"
            startIcon={<DownloadIcon />}
          >
            {localization.download}
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent className={classes.noPadding}>
        {preview}
      </DialogContent>
    </Dialog>
  );
}

function DocumentView(props: {
  doc: Document;
  size?: "medium" | "large" | "small" | undefined;
  hideBadge?: boolean;
  showSimilarity?: boolean;
}) {
  const classes = useStyles();
  const [showDetail, setShowDetail] = useState(false);
  const [index, setIndex] = React.useState(0);

  const buttonSize = props.size ? props.size : "small";

  const documentType = getDocumentType(props.doc.content_type)
  let dialog;
  if (documentType == 'image') {
    dialog = (
      <SupportedFileTypeDialog
        doc={props.doc}
        open={showDetail}
        onClose={() => setShowDetail(false)}
        showSimilarity={props.showSimilarity}
      />
    );
  }
  else if (documentType == 'pdf') {
    dialog = (
      <SupportedFileTypeDialog
        doc={props.doc}
        open={showDetail}
        onClose={() => setShowDetail(false)}
        showSimilarity={props.showSimilarity}
      />
    );
  }
  else {
    dialog = (
      <UnsupportedFileTypeDialog
        doc={props.doc}
        open={showDetail}
        onClose={() => setShowDetail(false)}
      />
    );
  }

  return (
    <>
      <Badge badgeContent={props.hideBadge ? 0 : props.doc.comments.length} color="secondary">
        <Button
          aria-label="download"
          size={buttonSize}
          startIcon={<IconByContentType contentType={props.doc.content_type} />}
          href={props.doc.file}
          target="_blank"
          onClick={(e) => {
            e.preventDefault();
            setShowDetail(true);
          }}
        >{props.doc.file_name}</Button>
      </Badge>
      {dialog}
    </>
  );
}

export {DocumentView};